import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import MarketingLayout from '@/components/MarketingLayout';

export default function ContactPage() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success(t('contact.form.success'));
      } else {
        throw new Error('Failed to send');
      }
    } catch (err) {
      toast.error(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: t('contact.info.email'),
      value: 'info@urenbuddy.nl',
      href: 'mailto:info@urenbuddy.nl'
    },
    {
      icon: Phone,
      label: t('contact.info.phone'),
      value: '+31 (0)20 123 4567',
      href: 'tel:+31201234567'
    },
    {
      icon: MapPin,
      label: t('contact.info.address'),
      value: 'Amsterdam, Nederland',
      href: null
    }
  ];

  const faqs = [
    {
      q: t('contact.faq.q1'),
      a: t('contact.faq.a1')
    },
    {
      q: t('contact.faq.q2'),
      a: t('contact.faq.a2')
    },
    {
      q: t('contact.faq.q3'),
      a: t('contact.faq.a3')
    }
  ];

  return (
    <MarketingLayout
      title={t('contact.meta.title')}
      description={t('contact.meta.description')}
      keywords="urenbuddy contact, zzp software support, klantenservice freelancer tools"
    >
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 pt-20 pb-24">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('contact.hero.title')}
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {/* Form */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                {submitted ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle className="w-8 h-8 text-teal-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-4">
                      {t('contact.form.successTitle')}
                    </h3>
                    <p className="text-slate-600">
                      {t('contact.form.successMessage')}
                    </p>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold text-slate-900 mb-6">
                      {t('contact.form.title')}
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">{t('contact.form.name')}</Label>
                          <Input
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder={t('contact.form.namePlaceholder')}
                            data-testid="contact-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">{t('contact.form.email')}</Label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder={t('contact.form.emailPlaceholder')}
                            data-testid="contact-email"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="subject">{t('contact.form.subject')}</Label>
                        <Input
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          required
                          placeholder={t('contact.form.subjectPlaceholder')}
                          data-testid="contact-subject"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="message">{t('contact.form.message')}</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          placeholder={t('contact.form.messagePlaceholder')}
                          data-testid="contact-message"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600"
                        data-testid="contact-submit"
                      >
                        {loading ? t('contact.form.sending') : t('contact.form.send')}
                        <Send className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">
                  {t('contact.info.title')}
                </h2>
                <div className="space-y-4">
                  {contactInfo.map((info, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500">{info.label}</div>
                        {info.href ? (
                          <a
                            href={info.href}
                            className="text-lg font-medium text-slate-900 hover:text-cyan-600"
                          >
                            {info.value}
                          </a>
                        ) : (
                          <div className="text-lg font-medium text-slate-900">
                            {info.value}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Response time */}
              <Card className="border-0 bg-teal-50">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <AlertCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-teal-900 mb-1">
                        {t('contact.info.responseTitle')}
                      </h3>
                      <p className="text-teal-700 text-sm">
                        {t('contact.info.responseText')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
            {t('contact.faq.title')}
          </h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
